import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";
import { useCallback, useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import "./App.css";
import { inject } from "../../../lib/itn-ui-kit/services/service-container";
import { setNotificationCount, setUIError } from "../../../state/actions";
import UnauthAccess from "../../../UnauthAccess";
import DataContext from "../../context/DataContext";
import { EventsDispatchContext, EventsStateContext } from "../../context/EventsProvider";
import useLiveServerEvents from "../../hooks/useLiveServerEvents";
import useLogout from "../../hooks/useLogout";
import ConditionsModal from "../../pods/login/components/ConditionsModal";
import ForceLogoutModal from "../../pods/login/components/ForceLogoutModal";
import { isPublicRoute } from "../../publicRoutes";
import axios from "../api/axios";
import Colors from "../global/Colors";
import { safeJsonParse } from "../global/JsonHelper";
import PatchUpdateDialog from "../global/PatchUpdateDialog";
import EmailModal from "../modules/Common/EmailModal";
import { useEmailModal } from "../modules/Common/EmailModal/store/useEmailModal";
import { HistoryLayout } from "../modules/Common/HistoryLayout";
import { useToastStore } from "../modules/Common/ToastProvider/store/useToastStore";
import { NetworkBackground } from "../NetworkBackground";
import MessageDialog from "../ui library/components/dialogs/MessageDialog";
import { userDBService } from "../ui library/helpers/dbInstances";

import { Banner } from "./Banner";

export default function App() {
  const currentUserService = inject("current-user-service");
  const {
    accessToken,
    setAccessToken,
    setUserType,
    setCaseManagerPk,
    setRefreshDialog,
    needsUpdate,
    setActiveLawfirm,
    setLawFirmLink,
    setUserRoles,
    setErrorMessage,
    userRoles,
    setPatchNotifs,
    patchNotifs,
    loggedInUser,
    setLoggedInUser,
    setLanguageObj,
    stopSse,
    setStopSse,
  } = useContext(DataContext);
  const { uiError, uiErrorMessage } = useContext(EventsStateContext);
  const globalEventDispatch = useContext(EventsDispatchContext);
  const [delay, setDelay] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);
  const logout = useLogout();
  const clearAllToasts = useToastStore((state) => state.clearAllToasts);
  const { openEmailModal } = useEmailModal();

  const handleConnectionError = useCallback(
    (message) => {
      setUIError(globalEventDispatch, {
        uiError: true,
        uiErrorMessage: message,
      });
    },
    [globalEventDispatch]
  );

  const handleCallSummaryNavigate = useCallback(() => navigate(`/ai-summary`), [navigate]);

  const handleNotificationCount = useCallback(
    (count) => {
      setNotificationCount(globalEventDispatch, Number.isFinite(count) ? count : 0);
    },
    [globalEventDispatch]
  );

  const registerSseStopper = useCallback(
    (stopper) => {
      setStopSse(() => stopper);
    },
    [setStopSse]
  );

  useLiveServerEvents({
    accessToken,
    permissions: userRoles?.permissions ?? [],
    onConnectionError: handleConnectionError,
    onCallSummaryNavigate: handleCallSummaryNavigate,
    onRegisterStopper: registerSseStopper,
    onNotificationCount: handleNotificationCount,
  });

  useEffect(() => {
    if (!accessToken && typeof clearAllToasts === "function") {
      clearAllToasts();
    }
  }, [accessToken, clearAllToasts]);

  useEffect(() => {
    const initCookies = async () => {
      await getAllCookies();
    };
    initCookies();
  }, [accessToken, isPublic]);

  const getAllCookies = async () => {
    if (!accessToken) {
      if (isPublic) {
        return;
      }
      navigate("/");
      setErrorMessage("You are not logged in, please log in to continue");
      return;
    }

    try {
      await getUserPerms();

      setAccessToken(accessToken);

      const cmPkCookie = Cookies.get("pnCmPk");
      if (cmPkCookie) {
        try {
          setCaseManagerPk(safeJsonParse(cmPkCookie));
        } catch (err) {
          Sentry.captureException(err);
          setCaseManagerPk(null);
        }
      } else {
        setCaseManagerPk(null);
      }

      setUserType(Cookies.get("userType") || null);

      getDashboardV();
      getLanguageObject();

      setTimeout(() => {
        setDelay(false);
      }, 2000);
    } catch (err) {
      Sentry.captureException(err);
      setErrorMessage(`Error in getAllCookies: ${err.message || String(err)}`);
    }
  };

  const getDashboardV = () => {
    if (!Cookies.get("dashVersion")) {
      return;
    } else {
      Cookies.remove("dashVersion");
    }
  };

  const getApiUrl = (apiUrl) => {
    //! Need to rework or get rid of this when there's time. it's only being used to determine if we need to send new client email or not. easily can move this into the places where emailJS is.
    if (apiUrl === "https://dev-api.healthnsync.com") {
      setActiveLawfirm("DEV TEST");
      setLawFirmLink("https://www.healthnsync.com/");
      console.info("Dev API");
    } else if (apiUrl === "https://staging-api.healthnsync.com") {
      setActiveLawfirm("STAGING TEST");
      setLawFirmLink("https://www.healthnsync.com/");
      console.info("Dev API");
    } else if (apiUrl === "https://api.healthnsync.com") {
      setActiveLawfirm("PRODUCTION");
      setLawFirmLink("https://app.healthnsync.com/");
      console.info("Production Loaded");
    } else {
      console.info("No API URL Found");
      setActiveLawfirm("Unknown");
      setLawFirmLink("#");
    }
  };

  const getUserPerms = async () => {
    const permissions = await userDBService.get("permissions");
    const userDetail = await userDBService.get("userDetail");
    currentUserService.user = userDetail;
    const groups = await userDBService.get("roles");
    const baseURL = await userDBService.get("baseURL");
    currentUserService.userRoles = { groups, permissions };

    if (permissions.length === 0) {
      Sentry.captureMessage(
        `No Permissions Assigned to User: ${currentUserService.user?.name}`,
        "fatal"
      );
      await logout();
      useToastStore.getState().clearQueue();
    }

    setUserRoles({ groups, permissions });
    getApiUrl(baseURL);
    setLoggedInUser(userDetail);
  };

  const compareAppVersion = () => {
    if (!userRoles || userRoles.length === 0 || patchNotifs || !accessToken) {
      return;
    }

    axios
      .get(`/api/utilities/new_versions/`)
      .then((response) => {
        const data = response?.data?.results ?? [];
        setPatchNotifs(data);
        const latestVersion = data[0]?.version_hash;
        const storedVersion = Cookies.get("patchStatus");

        if (!latestVersion) {
          Cookies.set("patchStatus", "");
        } else if (!storedVersion) {
          Cookies.set("patchStatus", latestVersion);
        } else if (latestVersion !== storedVersion) {
          Cookies.set("patchStatus", latestVersion);

          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      })
      .catch((error) => {
        setErrorMessage(`Versions Error: ${JSON.stringify(error)}`);
      });
  };

  const getLanguageObject = () => {
    if (!userRoles || !userRoles.permissions.includes("utilities.view_language")) {
      return;
    } else {
      axios
        .get(`/api/utilities/language/?ordering=created_at`)
        .then((response) => {
          const data = response.data.results;
          const obj = {};

          for (const item in data) {
            obj[data[item].pk] = data[item];
          }
          setLanguageObj(obj);
        })
        .catch(function (error) {
          if (error.response) {
            console.error(error.response);
          }
        });
    }
  };

  useEffect(() => {
    compareAppVersion();
  }, [accessToken, userRoles, patchNotifs]);

  if (isPublic) {
    return <Outlet />;
  }

  if (accessToken) {
    return delay ? (
      <div className="flex items-center justify-center w-screen h-screen">
        <NetworkBackground />
        <div className="flex flex-col items-center justify-center p-12 bg-white rounded-md shadow-md">
          <h1 className="text-lg font-medium">{accessToken ? "Page Loading" : "Logging in"}</h1>
          <br />
          <CircularProgress color="secondary" className="" />
        </div>
      </div>
    ) : (
      <div
        className="flex min-w-[60%] xl:overflow-hidden"
        style={{ backgroundColor: Colors.gray[100] }}
      >
        <div className="flex flex-col w-screen h-screen mt-16 overflow-y-auto sm:mt-0 xl:h-screen">
          {/* Banner */}
          <Banner />

          {/* Main */}
          <div className="flex flex-col flex-1" style={{ backgroundColor: Colors.gray[100] }}>
            <HistoryLayout />
          </div>
        </div>
        <PatchUpdateDialog />
        {needsUpdate ? (
          <div className="absolute right-[10px] top-[10px] block rounded-full bg-red-600">
            <Tooltip title="App Needs Update, please refresh">
              <IconButton onClick={() => setRefreshDialog(true)} className="text-white">
                <ReportProblemIcon />
              </IconButton>
            </Tooltip>
          </div>
        ) : null}
        <ConditionsModal />
        {/* Modal to force logout when token is expired */}
        <ForceLogoutModal />
        {/* Global emails modal */}
        {openEmailModal && <EmailModal />}
        <MessageDialog
          isError={uiError}
          message={uiErrorMessage}
          onClose={() =>
            setUIError(globalEventDispatch, {
              uiError: false,
              uiErrorMessage: "",
            })
          }
          open={uiError}
          title="Error"
        />
      </div>
    );
  }

  return <UnauthAccess status={401} />;
}
